/**
 * @cfleet/api-client - Retry With Backoff
 *
 * Small, dependency-free retry helper with exponential backoff. Used to make
 * token refresh resilient to transient network failures: a single blip should
 * not terminate a session. Mirrors the backoff shape already used by
 * `@cfleet/websocket`'s WebSocketProvider (initialDelay * multiplier^attempt).
 *
 * The caller decides what is retryable via `isRetryable`. Authoritative
 * failures (e.g. an HTTP 401) should return `false` so they short-circuit
 * immediately instead of wasting backoff attempts — a revoked session must log
 * the user out promptly, only transient failures should be retried.
 */

export interface RetryOptions {
  /** Total number of attempts, including the first. @default 3 */
  attempts?: number;
  /** Delay before the first retry, in milliseconds. @default 500 */
  initialDelayMs?: number;
  /** Backoff multiplier applied to the delay after each attempt. @default 2 */
  multiplier?: number;
  /**
   * Apply random jitter (full jitter: delay is multiplied by a random factor in
   * [0.5, 1]) to avoid synchronized retries across clients. @default true
   */
  jitter?: boolean;
  /**
   * Predicate deciding whether a thrown error should be retried. Return `false`
   * for authoritative failures so they propagate immediately. @default always true
   */
  isRetryable?: (error: unknown) => boolean;
  /** Invoked before each backoff wait (after a retryable failure). */
  onRetry?: (info: { attempt: number; error: unknown; delayMs: number }) => void;
  /** Injectable sleep, primarily for deterministic testing. */
  sleep?: (ms: number) => Promise<void>;
  /** Injectable RNG in [0, 1) for deterministic jitter testing. @default Math.random */
  random?: () => number;
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Run `fn`, retrying with exponential backoff on retryable failures.
 *
 * Resolves with the first successful result. Rejects with the last error once
 * attempts are exhausted, or immediately when `isRetryable` returns false.
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    attempts = 3,
    initialDelayMs = 500,
    multiplier = 2,
    jitter = true,
    isRetryable = () => true,
    onRetry,
    sleep = defaultSleep,
    random = Math.random,
  } = options;

  let lastError: unknown;

  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      const isLastAttempt = attempt >= attempts;
      if (isLastAttempt || !isRetryable(error)) {
        throw error;
      }

      const baseDelay = initialDelayMs * Math.pow(multiplier, attempt - 1);
      const delayMs = jitter ? baseDelay * (0.5 + random() * 0.5) : baseDelay;

      onRetry?.({ attempt, error, delayMs });
      await sleep(delayMs);
    }
  }

  // Unreachable: the loop either returns or throws, but satisfies the type checker.
  throw lastError;
}
